import { HttpParams } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { METRIC_BY_ID, METRICS_V2, THING_DEFINITION_METRICS_V2, USER_METRIC_SETS } from '../common/endpoints';
import { PagedList } from '../model';
import { Metric, MetricCategory, SystemMetric } from '../model/metric';
import { MetricSet } from '../model/metric-set';
import { MetricDetailComponent } from '../shared/component/metric/metric-detail.component';
import { HttpService } from './http.service';
import { ThingDefinitionService } from './thing-definition.service';

@Injectable()
export class MetricService {

    static SystemMetricDefaultFilter = new Map<String, String>([
        [SystemMetric.CONNECTION_STATUS_METRIC_NAME, 'defaultConnectionStatus'],
        [SystemMetric.CLOUD_STATUS_METRIC_NAME, 'defaultCloudStatus']
    ]);

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => ThingDefinitionService)) private thingDefinitionService: ThingDefinitionService
    ) { }

    getMetricsByThingDefinitionId(thingDefinitionId: string, includeInherited: boolean = true, params?: HttpParams): Promise<Metric[]> {
        let metrics: Metric[] = [];
        let page = 0;
        return this.getRecursivelyAllPages(thingDefinitionId, page, metrics, includeInherited, params);
    }

    private getRecursivelyAllPages(thingDefinitionId: string, page: number, metrics: Metric[], includeInherited: boolean = true, params?: HttpParams): Promise<Metric[]> {
        return this.getPagedMetricsByThingDefinitionId(thingDefinitionId, includeInherited, MetricCategory.ALL, null, page, 100, ['name', 'asc'], params)
            .then(pagedMetrics => {
                metrics = metrics.concat(pagedMetrics.content);
                if (pagedMetrics.last) {
                    return metrics;
                } else {
                    return this.getRecursivelyAllPages(thingDefinitionId, ++page, metrics);
                }
            });
    }

    getPagedMetricsByThingDefinitionId(thingDefinitionId: string, includeInherited: boolean, category: MetricCategory, searchText: string, page: number, size: number, sort: string[], params?: HttpParams): Promise<PagedList<Metric>> {
        if (!params) {
            params = new HttpParams();
        }
        params = params.set('page', page + '');
        params = params.set('size', size + '');
        params = params.set('category', category + "");
        if (sort && sort[0]) {
            params = params.set('sort', sort.join(','));
        }
        if (includeInherited) {
            params = params.set('includeInherited', includeInherited + "");
        }
        if (searchText) {
            params = params.set('searchText', '*' + searchText + '*');
        }
        return firstValueFrom(this.httpService.get<PagedList<Metric>>(THING_DEFINITION_METRICS_V2.replace('{id}', thingDefinitionId), params));
    }

    static getMetricFilter(metricComponent: MetricDetailComponent): string {
        if (metricComponent.filter) {
            return metricComponent.filter;
        } else {
            const metricName = metricComponent.name;
            let defaultFilter;
            if (metricName === SystemMetric.CONNECTION_STATUS_METRIC_NAME) {
                defaultFilter = 'defaultConnectionStatus';
            } else if (metricName === SystemMetric.CLOUD_STATUS_METRIC_NAME) {
                defaultFilter = 'defaultCloudStatus';
            }
            return defaultFilter;
        }
    }

    static extractMetricName(name: string) {
        const regex = /\(\w+(\D?\w)*\)/;
        if (regex.test(name)) {
            const first = regex.exec(name)[0];
            return first.slice(1, first.length - 1);
        }
        return name;
    }

    getMetricByName(name: string, thingDefinitionId?: string): Promise<Metric> {
        let params = new HttpParams();
        params = params.set('page', '0');
        params = params.set('size', '1');
        params = params.set('sort', 'name,ASC');
        params = params.set('name', 'eq;' + name);
        if (thingDefinitionId) {
            params = params.set('thingDefinitionId', thingDefinitionId);
        }
        return firstValueFrom(this.httpService.get<PagedList<Metric>>(METRICS_V2, params)).then(pagedMetrics => {
            return pagedMetrics.content.length > 0 ? pagedMetrics.content[0] : null;
        });
    }

    getMetricById(metricId: string): Observable<Metric> {
        return this.httpService.get<Metric>(METRIC_BY_ID.replace('{id}', metricId));
    }

    getMetricSets(): Promise<MetricSet[]> {
        return firstValueFrom(this.httpService.get<MetricSet[]>(USER_METRIC_SETS));
    }

    getAllMetricsAssociatedToThings(params?: HttpParams): Promise<Metric[]> {
        return this.thingDefinitionService.getThingDefinitionsAssociatedToThings(params).then(thingDefinitions => {
            let promises = [];
            thingDefinitions.forEach(thingDef => {
                promises.push(this.getMetricsByThingDefinitionId(thingDef.id).then(metrics => {
                    return metrics;
                }));
            });
            return Promise.all(promises).then((results: Metric[][]) => {
                return [].concat(...results);
            });
        });
    }
}